import type { TaskItem } from '@hygge/shared';

/** Sentinel used by the "Unassigned" quick filter and the assignee picker. */
export const UNASSIGNED = '__unassigned__';

/**
 * Client-side filter model. The dimension arrays (statusIds, priorities, types,
 * assigneeIds, labels) + `overdue` are applied locally against the loaded task
 * set — an empty array means "no constraint on this dimension". `search` and
 * `sprintId` remain server-side (see `toQueryFilters` in task-filters.tsx).
 */
export interface FilterState {
  search: string;
  statusIds: string[];
  priorities: string[];
  types: string[];
  assigneeIds: string[]; // may include the UNASSIGNED sentinel
  labels: string[];
  overdue: boolean;
  sprintId: string; // stays server-side; 'all' | 'none' | <id>
}

export const EMPTY_FILTERS: FilterState = {
  search: '',
  statusIds: [],
  priorities: [],
  types: [],
  assigneeIds: [],
  labels: [],
  overdue: false,
  sprintId: 'all',
};

export function emptyFilters(): FilterState {
  return {
    ...EMPTY_FILTERS,
    statusIds: [],
    priorities: [],
    types: [],
    assigneeIds: [],
    labels: [],
  };
}

/** Count of active client-side dimensions (excludes search + sprint, which are server-side). */
export function activeFilterCount(f: FilterState): number {
  return (
    (f.statusIds.length ? 1 : 0) +
    (f.priorities.length ? 1 : 0) +
    (f.types.length ? 1 : 0) +
    (f.assigneeIds.length ? 1 : 0) +
    (f.labels.length ? 1 : 0) +
    (f.overdue ? 1 : 0)
  );
}

export function hasActiveClientFilters(f: FilterState): boolean {
  return activeFilterCount(f) > 0;
}

/**
 * A task is overdue when it has a due date in the past and is not in a `done`
 * status. `todayIso` is 'YYYY-MM-DD'.
 */
export function isOverdue(task: TaskItem, todayIso: string): boolean {
  if (!task.dueDate) return false;
  const due = task.dueDate.slice(0, 10);
  const done = task.status?.category === 'done';
  return !done && due < todayIso;
}

/**
 * Pure client-side match for the dimension filters. Empty array on a dimension =
 * no constraint. Filters OR within a dimension and AND across dimensions.
 * Assignee accepts the UNASSIGNED sentinel (task with no assignee).
 * `todayIso` is 'YYYY-MM-DD' (pass new Date().toISOString().slice(0,10) at the call site).
 */
export function taskMatchesFilters(task: TaskItem, f: FilterState, todayIso: string): boolean {
  if (f.statusIds.length && !f.statusIds.includes(task.status?.id ?? task.statusId)) return false;
  if (f.priorities.length && !f.priorities.includes(task.priority)) return false;
  if (f.types.length && !f.types.includes(task.type)) return false;
  if (f.assigneeIds.length) {
    // Match against the FULL assignee set (any-of). Fall back to the denormalized
    // primary when `assignees` is absent (older payloads / optimistic updates).
    const primary = task.assignee?.id ?? task.assigneeId ?? null;
    const aids = task.assignees?.length
      ? task.assignees.map((a) => a.id)
      : primary !== null
        ? [primary]
        : [];
    const matchesUnassigned = f.assigneeIds.includes(UNASSIGNED) && aids.length === 0;
    const matchesId = aids.some((id) => f.assigneeIds.includes(id));
    if (!matchesUnassigned && !matchesId) return false;
  }
  if (f.labels.length) {
    const tagNames = new Set<string>([
      ...(task.labels ?? []),
      ...((task.tags ?? []).map((t) => t.name)),
    ]);
    if (!f.labels.some((l) => tagNames.has(l))) return false;
  }
  if (f.overdue && !isOverdue(task, todayIso)) return false;
  return true;
}

// ─── Pure slice setters (used by quick filters + chips) ──────────────────

/** Toggle a scalar value's membership in a string-array dimension. */
export function toggleInArray(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}
